import React, { useEffect } from 'react';
import { FaPen, FaTrash, FaExclamationCircle } from 'react-icons/fa';
import {
  IconButton,
  Tr,
  Td,
  HStack,
  Icon,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { REMOVE_PAGE } from '../../../../graphQL/mutations';
import { GET_PAGES } from '../../../../graphQL/queries';
import useAuth from '../../../../hooks/useAuth';

interface Props {
  page: any;
  onDelete?: (pageId: any) => void;
  onEditClick?: (page: any) => void;
}

const Item: React.FC<Props> = ({ page, onDelete, onEditClick }) => {
  const toast = useToast();

  const [removePage, { loading: removeLoading, error: removeError }] =
    useMutation(REMOVE_PAGE, {
      refetchQueries: [GET_PAGES, 'GetPages'],
    });

  const { nonEditablePages } = useAuth();
  const { t } = useTranslation();
  const handlePageDelete = async (pageId: string) => {
    if (nonEditablePages?.includes(page.slug)) {
      toast({
        title: 'Opss!!',
        description: t('notPossibleRemoveHomePage'),
        status: 'error',
        position: 'top-right',
      });
    } else {
      try {
        if (window.confirm(t('askDeleteShowcase'))) {
          await removePage({
            variables: {
              id: pageId,
            },
          });
          toast({
            title: t('success'),
            description: t('deletedShowcase'),
            status: 'success',
            position: 'top-right',
          });
          if (onDelete) {
            onDelete(pageId);
          }
        }
      } catch (err: any) {
        toast({
          title: 'Opss!!',
          description:
            err?.response?.data?.message || err?.message || t('generalError'),
          status: 'error',
          position: 'top-right',
        });
      }
    }
  };

  useEffect(() => {
    if (removeError) {
      toast({
        title: 'Opss!!',
        description: removeError?.message || t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    }
  }, [removeError, toast, t]);

  return (
    <>
      <Tr bg="#FFFFFF">
        <Td>{page?.name}</Td>
        <Td>{page?.vtex_query}</Td>
        <Td>
          <HStack spacing={3}>
            {!nonEditablePages?.includes(page.slug) ? (
              <>
                <IconButton
                  color="#CECECE"
                  aria-label="edit-page"
                  rounded="md"
                  icon={<FaPen size={17} />}
                  size="xs"
                  colorScheme="#FFFFFF"
                  isLoading={removeLoading}
                  onClick={() => {
                    if (onEditClick) {
                      onEditClick(page);
                    }
                  }}
                />

                <IconButton
                  aria-label="delete-page"
                  rounded="md"
                  color="#FF6565"
                  icon={<FaTrash size={17} />}
                  size="xs"
                  colorScheme="#FFFFFF"
                  isLoading={removeLoading}
                  onClick={() => {
                    handlePageDelete(page?.id);
                  }}
                />
              </>
            ) : (
              <Tooltip hasArrow label={t('nonEditablePagesAlertMessage')}>
                <span>
                  <Icon w={5} h={5} as={FaExclamationCircle} color="blue.500" />
                </span>
              </Tooltip>
            )}
          </HStack>
        </Td>
      </Tr>
    </>
  );
};

export default Item;
